import { isShopPdpRoute } from '~/utils/shop/isShopPdpRoute';

/** Window after leaving a PDP where late fatal errors from stale init are cleared. */
const STALE_PDP_ERROR_CLEAR_MS = 4000;

/**
 * Clears a persisted Nuxt fatal error when leaving a PDP or when client navigation
 * succeeds (e.g. browser back while PDP init was still in flight).
 *
 * The PDP store is only loaded when the user actually leaves a PDP, so it is not
 * pulled into the eager entry chunk nor loaded on pages that never visit a PDP.
 */
export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter();
  let leftPdpAt = 0;

  router.beforeEach(async (to, from) => {
    if (isShopPdpRoute(from) && !isShopPdpRoute(to)) {
      leftPdpAt = Date.now();
      const { useProductDetailPageStore } =
        await import('~/stores/productDetailPageStore/productDetailPageStore');
      useProductDetailPageStore().cancelPendingLoads();

      const error = useError();
      if (error.value) {
        clearError();
      }
    }
  });

  router.afterEach((_to, _from, failure) => {
    // The initial navigation runs while hydrating an SSR error page (e.g. an
    // unknown PDP url key). Clearing it there swaps the 404 back to the page
    // template until the client re-throws. Nuxt's own router skips this too.
    if (failure || nuxtApp.isHydrating) {
      return;
    }

    const error = useError();
    if (error.value) {
      clearError();
    }
  });

  watch(
    () => useError().value,
    (error) => {
      if (!error || isShopPdpRoute(router.currentRoute.value)) {
        return;
      }

      if (Date.now() - leftPdpAt < STALE_PDP_ERROR_CLEAR_MS) {
        clearError();
      }
    }
  );
});
